import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Plus, Pencil, Trash2, Music, Sparkles, Star, Megaphone, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeOptions = [
  { value: 'audio', label: 'Nouveau contenu audio', icon: Music, color: 'text-purple-500' },
  { value: 'improvement', label: 'Amélioration', icon: Sparkles, color: 'text-blue-500' },
  { value: 'new_feature', label: 'Nouveauté', icon: Star, color: 'text-amber-500' },
  { value: 'general', label: 'Annonce générale', icon: Megaphone, color: 'text-sage-500' }
];

export const AdminAnnouncements = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    announcement_type: 'general'
  });

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des annonces');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [token]);

  const openCreateModal = () => {
    setEditingAnnouncement(null);
    setFormData({ title: '', content: '', announcement_type: 'general' });
    setIsModalOpen(true);
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      announcement_type: announcement.announcement_type
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnnouncement) {
        await axios.put(`${API_URL}/admin/announcements/${editingAnnouncement.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Annonce mise à jour');
      } else {
        await axios.post(`${API_URL}/admin/announcements`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Annonce créée');
      }
      setIsModalOpen(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const toggleActive = async (announcement) => {
    try {
      await axios.put(`${API_URL}/admin/announcements/${announcement.id}`, {
        is_active: !announcement.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(announcement.is_active ? 'Annonce désactivée' : 'Annonce activée');
      fetchAnnouncements();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce supprimée');
      fetchAnnouncements();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getTypeConfig = (type) => {
    return typeOptions.find(t => t.value === type) || typeOptions[3];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50" data-testid="admin-announcements-page">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-white hover:bg-sage-600"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-serif font-light">Annonces</h1>
          </div>
          <Button
            onClick={openCreateModal}
            className="bg-declic hover:bg-declic/90 text-white"
            data-testid="create-announcement-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle annonce
          </Button>
        </div>
      </div>

      {/* Liste des annonces */}
      <div className="p-6 space-y-4">
        {announcements.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Megaphone className="w-12 h-12 text-sage-300 mx-auto mb-3" />
            <p className="text-sage-500">Aucune annonce créée</p>
            <p className="text-sage-400 text-sm">Créez votre première annonce pour informer vos utilisateurs</p>
          </div>
        ) : (
          announcements.map((announcement) => {
            const typeConfig = getTypeConfig(announcement.announcement_type);
            const Icon = typeConfig.icon;
            
            return (
              <div
                key={announcement.id}
                className={`bg-white rounded-xl p-5 border ${
                  announcement.is_active ? 'border-sand-200' : 'border-red-200 bg-red-50/30'
                }`}
                data-testid={`announcement-item-${announcement.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg bg-sage-100`}>
                      <Icon className={`w-5 h-5 ${typeConfig.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${typeConfig.color}`}>
                          {typeConfig.label}
                        </span>
                        {!announcement.is_active && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                            Désactivée
                          </span>
                        )}
                      </div>
                      <h3 className="font-sans font-medium text-sage-700 mb-1">
                        {announcement.title}
                      </h3>
                      <p className="text-sm text-sage-500 line-clamp-2">
                        {announcement.content}
                      </p>
                      <p className="text-xs text-sage-400 mt-2">
                        Créée le {formatDate(announcement.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleActive(announcement)}
                      className="text-sage-500 hover:text-sage-700"
                      title={announcement.is_active ? 'Désactiver' : 'Activer'}
                    >
                      {announcement.is_active ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditModal(announcement)}
                      className="text-sage-500 hover:text-sage-700"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteAnnouncement(announcement.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de création/édition */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md mx-4 bg-white rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif font-light text-sage-700">
              {editingAnnouncement ? 'Modifier l\'annonce' : 'Nouvelle annonce'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-sage-700 mb-2">
                Type d'annonce
              </label>
              <div className="grid grid-cols-2 gap-2">
                {typeOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, announcement_type: option.value })}
                      className={`p-3 rounded-xl border text-left transition-colors ${
                        formData.announcement_type === option.value
                          ? 'border-sage-500 bg-sage-50'
                          : 'border-sand-200 hover:border-sage-300'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${option.color} mb-1`} />
                      <span className="text-sm text-sage-700">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-sage-700 mb-2">
                Titre
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Nouveau thème disponible !"
                required
                className="border-sand-200"
              />
            </div>

            {/* Contenu */}
            <div>
              <label className="block text-sm font-medium text-sage-700 mb-2">
                Contenu (Markdown supporté)
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Décrivez la nouveauté..."
                rows={4}
                required
                className="border-sand-200"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-sage-600 hover:bg-sage-600 text-white"
              >
                {editingAnnouncement ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAnnouncements;
